/**
 * 集計ページ共通の期間切替(日次／週次／月次)とデータ読み込み(⑥指示)。
 * 期間はURL(?mode=day|week|month&date=YYYY-MM-DD)に持ち、PL／会社別／出勤形態別／スタッフ別の
 * どのページへ移動しても同じ期間が保たれる。
 *  - 日次: その日 / 週次: 月曜〜日曜(月曜始まり・月内通番 W1〜W6) / 月次: 1日〜末日
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '@core/components/Toast';
import { todayStr, weekdayLabel } from '@core/lib/date';
import type { Company, DailySummary, DateStr, Delivery, MonthTargets } from '@core/lib/types';
import { fetchCompanies } from '@core/masters/companyRepo';
import { addDays, addMonths, daysInMonth, staffMetrics, weekOfMonth, type StaffMetrics } from './calc';
import { fetchLatestSummaryDate, fetchMonthTargets, fetchSummariesRange } from './dashboardRepo';
import { fetchDeliveries } from './deliveryRepo';

/** year は PL の「月ごと」用(その年の1月〜12月)。期間ツールバーのボタンには出さない */
export type PeriodMode = 'day' | 'week' | 'month' | 'year';
export const PERIOD_LABELS: Record<'day' | 'week' | 'month', string> = { day: '日次', week: '週次', month: '月次' };

export type PeriodRange = {
  mode: PeriodMode;
  anchor: DateStr;
  from: DateStr;
  to: DateStr;
  /** 画面表示用(例: 2026/09/09(水) / 2026/09/07〜09/13 W2 / 2026年9月) */
  label: string;
  /** 期間内の全日付 */
  dates: DateStr[];
};

function dow(date: DateStr): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

export function rangeOf(mode: PeriodMode, anchor: DateStr): PeriodRange {
  let from = anchor;
  let to = anchor;
  let label = `${anchor.replaceAll('-', '/')}${weekdayLabel(anchor)}`;
  if (mode === 'week') {
    const back = (dow(anchor) + 6) % 7; // 月曜=0
    from = addDays(anchor, -back);
    to = addDays(from, 6);
    label = `${from.replaceAll('-', '/')}〜${to.slice(5).replace('-', '/')} (W${weekOfMonth(from)})`;
  } else if (mode === 'month') {
    const month = anchor.slice(0, 7);
    from = `${month}-01`;
    to = `${month}-${String(daysInMonth(month)).padStart(2, '0')}`;
    label = `${month.slice(0, 4)}年${Number(month.slice(5))}月`;
  } else if (mode === 'year') {
    const year = anchor.slice(0, 4);
    from = `${year}-01-01`;
    to = `${year}-12-31`;
    label = `${year}年`;
  }
  const dates: DateStr[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) dates.push(d);
  return { mode, anchor, from, to, label, dates };
}

export function shiftAnchor(mode: PeriodMode, anchor: DateStr, delta: number): DateStr {
  if (mode === 'day') return addDays(anchor, delta);
  if (mode === 'week') return addDays(anchor, delta * 7);
  if (mode === 'year') return `${Number(anchor.slice(0, 4)) + delta}-01-01`;
  return `${addMonths(anchor.slice(0, 7), delta)}-01`;
}

export type PeriodData = {
  mode: PeriodMode;
  setMode: (m: PeriodMode) => void;
  anchor: DateStr;
  setAnchor: (d: DateStr) => void;
  range: PeriodRange;
  loading: boolean;
  summaries: DailySummary[];
  targets: MonthTargets | null;
  companies: Company[];
  companyMap: Map<string, Company>;
  deliveries: Delivery[];
  /** 期間内の全行(BB ID×割振会社×日)。売上単価は会社マスタの改訂履歴からその日の単価 */
  rows: StaffMetrics[];
  /** 集計データがある最新日 */
  latest: DateStr | null;
  reload: () => Promise<void>;
};

export function usePeriodData(): PeriodData {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const mode = ((params.get('mode') as PeriodMode | null) ?? 'day') as PeriodMode;
  const anchor = params.get('date') ?? todayStr();
  const range = useMemo(() => rangeOf(mode, anchor), [mode, anchor]);

  const [summaries, setSummaries] = useState<DailySummary[]>([]);
  const [targets, setTargets] = useState<MonthTargets | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [latest, setLatest] = useState<DateStr | null>(null);
  const [loading, setLoading] = useState(true);

  const setMode = useCallback(
    (m: PeriodMode) => {
      const next = new URLSearchParams(params);
      next.set('mode', m);
      next.set('date', anchor);
      setParams(next, { replace: true });
    },
    [params, anchor, setParams],
  );
  const setAnchor = useCallback(
    (d: DateStr) => {
      const next = new URLSearchParams(params);
      next.set('mode', mode);
      next.set('date', d);
      setParams(next, { replace: true });
    },
    [params, mode, setParams],
  );

  // 初回: 日付未指定なら集計データのある最新日にする
  useEffect(() => {
    void fetchLatestSummaryDate()
      .then((d) => {
        setLatest(d);
        if (d && !params.get('date')) {
          const next = new URLSearchParams(params);
          next.set('mode', mode);
          next.set('date', d);
          setParams(next, { replace: true });
        }
      })
      .catch(() => undefined); // 最新日は初期表示の補助なので失敗しても続行
    void fetchCompanies()
      .then(setCompanies)
      .catch(() => toast('会社マスタの読み込みに失敗しました。再読み込みしてください'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t, d] = await Promise.all([
        fetchSummariesRange(range.from, range.to),
        fetchMonthTargets(range.anchor.slice(0, 7)),
        fetchDeliveries(range.from, range.to),
      ]);
      setSummaries(s);
      setTargets(t);
      setDeliveries(d);
    } catch (e) {
      toast(e instanceof Error ? e.message : '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, range.anchor, toast]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const companyMap = useMemo(() => new Map(companies.map((c) => [c.id, c])), [companies]);
  const rows = useMemo(() => {
    if (!targets) return [];
    return summaries.flatMap((s) => s.staff.map((st) => staffMetrics(st, s, targets, companyMap)));
  }, [summaries, targets, companyMap]);

  return { mode, setMode, anchor, setAnchor, range, loading, summaries, targets, companies, companyMap, deliveries, rows, latest, reload };
}
